import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FormDialog } from "@/components/common/Dialog";
import { Input } from "@/components/common/Input";
import { Label } from "@/components/common/FormLabel";
import { ErrorLabel } from "@/components/common/ErrorLabel";
import { Select } from "@/components/common/Select";
import { Spinner } from "@/components/common/Loading";
import { UserDefinedItemField, AddressField, EmailField } from "@/components/common/FormFields";
import { fetchQuickCreateContactOptions, quickCreateContact } from "@/api/contact/api";
import { searchContactUdLookup } from "@/api/autocomplete/api";
import { toSelectOptions } from "@/utils/fieldValidation";
import { showErrorToast, showSuccessToast } from "@/hooks/use-toast";
import { QuickCreateContactOptions, ContactFieldOptions } from "@/types/domain";
import { collectUserDefinedValues } from "@/utils/reactHookForm";

type FormValues = {
  firstName: string;
  surname: string;
  preferredName: string;
  title: string;
  jobDescription: string;
  department: string;
  email: string;
  telephoneOffice: string;
  telephoneHome: string;
  telephoneMobile: string;
  fax: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  town: string;
  county: string;
  country: string;
  postcode: string;
  comments: string;
  primaryContact: string;
  [key: string]: string | boolean;
};

type QuickContactModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  onContactCreated: (contactId: string, contactName: string) => void;
};

const mandatoryLabel = (label: string) => (
  <>
    {label} <span className="text-red-500">*</span>
  </>
);

export const QuickContactModal = ({ open, onOpenChange, customerId, onContactCreated }: QuickContactModalProps) => {
  const [options, setOptions] = useState<QuickCreateContactOptions | null>(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { register, handleSubmit, reset, setValue, control, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      firstName: "", surname: "", preferredName: "", title: "",
      jobDescription: "", department: "", email: "",
      telephoneOffice: "", telephoneHome: "", telephoneMobile: "", fax: "",
      address1: "", address2: "", address3: "", address4: "",
      town: "", county: "", country: "", postcode: "",
      comments: "", primaryContact: "0",
    },
  });

  useEffect(() => {
    if (!open || !customerId) return;

    setIsLoadingOptions(true);

    fetchQuickCreateContactOptions(customerId)
      .then((data) => setOptions(data))
      .catch(() => {
        showErrorToast("Quick Contact could not load.");
        onOpenChange(false);
      })
      .finally(() => setIsLoadingOptions(false));
  }, [open, customerId, onOpenChange]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      reset();
      setServerError(null);
      setIsLoadingOptions(false);
      setOptions(null);
    }
    onOpenChange(isOpen);
  };

  const handleCopyCompanyAddress = () => {
    if (!options) return;
    setValue("address1", options.companyAddress1 ?? "");
    setValue("address2", options.companyAddress2 ?? "");
    setValue("address3", options.companyAddress3 ?? "");
    setValue("address4", options.companyAddress4 ?? "");
    setValue("town", options.companyTown ?? "");
    setValue("county", options.companyCounty ?? "");
    setValue("country", options.companyCountry ?? "");
    setValue("postcode", options.companyPostcode ?? "");
  };

  const hasCompanyAddress = (opts: QuickCreateContactOptions) =>
    !!(
      opts.companyAddress1 ||
      opts.companyTown ||
      opts.companyPostcode
    );

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    setIsSaving(true);

    try {
      const userDefinedValues = collectUserDefinedValues(options?.userDefinedGroups, values);

      const result = await quickCreateContact({
        customerId,
        firstName: values.firstName,
        surname: values.surname,
        preferredName: values.preferredName || undefined,
        title: values.title || undefined,
        jobDescription: values.jobDescription || undefined,
        department: values.department || undefined,
        email: values.email || undefined,
        telephoneOffice: values.telephoneOffice || undefined,
        telephoneHome: values.telephoneHome || undefined,
        telephoneMobile: values.telephoneMobile || undefined,
        fax: values.fax || undefined,
        address1: values.address1 || undefined,
        address2: values.address2 || undefined,
        address3: values.address3 || undefined,
        address4: values.address4 || undefined,
        town: values.town || undefined,
        county: values.county || undefined,
        country: values.country || undefined,
        postcode: values.postcode || undefined,
        comments: values.comments || undefined,
        primaryContact: parseInt(values.primaryContact, 10) || 0,
        userDefinedValues: userDefinedValues.length > 0 ? userDefinedValues : undefined,
      });

      if (result.success && result.contactId && result.contactName) {
        showSuccessToast("Contact added successfully.");
        onContactCreated(result.contactId, result.contactName);
        handleOpenChange(false);
      } else {
        setServerError(result.errorMessage ?? "Failed to create contact.");
      }
    } catch {
      setServerError("An unexpected error occurred.");
    } finally {
      setIsSaving(false);
    }
  });

  const fieldRules = (opts: ContactFieldOptions, label: string) =>
    opts.isMandatory ? { required: `${label} is required` } : {};

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Quick Contact"
      onSubmit={onSubmit}
      submitText="Create Contact"
      loading={isSaving}
      disableSubmit={isLoadingOptions}
      hideTrigger={true}
      disableCloseOnOutsideClick={true}
    >
      <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto pr-1">
        {isLoadingOptions ? (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {serverError && (
              <p className="text-sm text-red-600">{serverError}</p>
            )}

            {/* First Name — always required */}
            <div>
              <Label forField="ac-first-name" label={mandatoryLabel("First Name")} />
              <Input
                id="ac-first-name"
                type="text"
                maxLength={50}
                inErrorState={Boolean(errors.firstName)}
                {...register("firstName", {
                  required: "First name is required",
                  maxLength: { value: 50, message: "First name must be 50 characters or less" },
                })}
              />
              <ErrorLabel error={errors.firstName?.message} />
            </div>

            {/* Surname — always required */}
            <div>
              <Label forField="ac-surname" label={mandatoryLabel("Surname")} />
              <Input
                id="ac-surname"
                type="text"
                maxLength={50}
                inErrorState={Boolean(errors.surname)}
                {...register("surname", {
                  required: "Surname is required",
                  maxLength: { value: 50, message: "Surname must be 50 characters or less" },
                })}
              />
              <ErrorLabel error={errors.surname?.message} />
            </div>

            {/* Preferred Name */}
            <div>
              <Label forField="ac-preferred-name" label="Preferred Name" />
              <Input
                id="ac-preferred-name"
                type="text"
                maxLength={50}
                {...register("preferredName", {
                  maxLength: { value: 50, message: "Preferred name must be 50 characters or less" },
                })}
              />
            </div>

            {/* Title */}
            <div>
              <Label forField="ac-title" label="Title" />
              <Input
                id="ac-title"
                type="text"
                maxLength={50}
                {...register("title", {
                  maxLength: { value: 50, message: "Title must be 20 characters or less" },
                })}
              />
            </div>

            {/* Job Description */}
            {options && options.jobDescription.isMandatory && (
              <div>
                <Label
                  forField="ac-job-description"
                  label={options.jobDescription.isMandatory ? mandatoryLabel("Job Description") : "Job Description"}
                />
                <Input
                  id="ac-job-description"
                  type="text"
                  maxLength={50}
                  disabled={options.jobDescription.isReadOnly}
                  inErrorState={Boolean(errors.jobDescription)}
                  {...register("jobDescription", {
                    ...fieldRules(options.jobDescription, "Job Description"),
                    maxLength: { value: 50, message: "Job Description must be 50 characters or less" },
                  })}
                />
                <ErrorLabel error={errors.jobDescription?.message} />
              </div>
            )}

            {/* Department */}
            {options && options.department.isMandatory && (
              <div>
                <Label
                  forField="ac-department"
                  label={options.department.isMandatory ? mandatoryLabel("Department") : "Department"}
                />
                <Input
                  id="ac-department"
                  type="text"
                  maxLength={50}
                  disabled={options.department.isReadOnly}
                  inErrorState={Boolean(errors.department)}
                  {...register("department", {
                    ...fieldRules(options.department, "Department"),
                    maxLength: { value: 50, message: "Department must be 50 characters or less" },
                  })}
                />
                <ErrorLabel error={errors.department?.message} />
              </div>
            )}

            {/* Email */}
            {options && (
              <EmailField
                fieldKey="email"
                id="ac-email"
                isMandatory={options.email.isMandatory}
                isReadOnly={options.email.isReadOnly}
                register={register}
                errors={errors}
              />
            )}

            {/* Telephone Office */}
            {options && (
              <div>
                <Label
                  forField="ac-tel-office"
                  label={options.telephoneOffice.isMandatory ? mandatoryLabel("Telephone (Office)") : "Telephone (Office)"}
                />
                <Input
                  id="ac-tel-office"
                  type="text"
                  maxLength={30}
                  disabled={options.telephoneOffice.isReadOnly}
                  inErrorState={Boolean(errors.telephoneOffice)}
                  {...register("telephoneOffice", {
                    ...fieldRules(options.telephoneOffice, "Telephone (Office)"),
                    maxLength: { value: 30, message: "Telephone must be 30 characters or less" },
                  })}
                />
                <ErrorLabel error={errors.telephoneOffice?.message} />
              </div>
            )}

            {/* Telephone Home */}
            {options && (
              <div>
                <Label
                  forField="ac-tel-home"
                  label={options.telephoneHome.isMandatory ? mandatoryLabel("Telephone (Home)") : "Telephone (Home)"}
                />
                <Input
                  id="ac-tel-home"
                  type="text"
                  maxLength={30}
                  disabled={options.telephoneHome.isReadOnly}
                  inErrorState={Boolean(errors.telephoneHome)}
                  {...register("telephoneHome", {
                    ...fieldRules(options.telephoneHome, "Telephone (Home)"),
                    maxLength: { value: 30, message: "Telephone must be 30 characters or less" },
                  })}
                />
                <ErrorLabel error={errors.telephoneHome?.message} />
              </div>
            )}

            {/* Telephone Mobile */}
            {options && (
              <div>
                <Label
                  forField="ac-tel-mobile"
                  label={options.telephoneMobile.isMandatory ? mandatoryLabel("Telephone (Mobile)") : "Telephone (Mobile)"}
                />
                <Input
                  id="ac-tel-mobile"
                  type="text"
                  maxLength={30}
                  disabled={options.telephoneMobile.isReadOnly}
                  inErrorState={Boolean(errors.telephoneMobile)}
                  {...register("telephoneMobile", {
                    ...fieldRules(options.telephoneMobile, "Telephone (Mobile)"),
                    maxLength: { value: 30, message: "Telephone must be 30 characters or less" },
                  })}
                />
                <ErrorLabel error={errors.telephoneMobile?.message} />
              </div>
            )}

            {/* Fax */}
            {options && options.fax.isMandatory && (
              <div>
                <Label
                  forField="ac-fax"
                  label={options.fax.isMandatory ? mandatoryLabel("Fax") : "Fax"}
                />
                <Input
                  id="ac-fax"
                  type="text"
                  maxLength={30}
                  disabled={options.fax.isReadOnly}
                  inErrorState={Boolean(errors.fax)}
                  {...register("fax", {
                    ...fieldRules(options.fax, "Fax"),
                    maxLength: { value: 30, message: "Fax must be 30 characters or less" },
                  })}
                />
                <ErrorLabel error={errors.fax?.message} />
              </div>
            )}

            {/* Address */}
            {options && (
              <AddressField
                idPrefix="ac-"
                register={register}
                errors={errors}
                isMandatory={options.address.isMandatory}
                isReadOnly={options.address.isReadOnly}
                header={
                  <div className="flex items-center justify-between mb-1">
                    <Label
                      forField="ac-address1"
                      label={options.address.isMandatory ? mandatoryLabel("Address") : "Address"}
                    />
                    {!options.address.isReadOnly && hasCompanyAddress(options) && (
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        onClick={handleCopyCompanyAddress}
                      >
                        Copy Company Address
                      </button>
                    )}
                  </div>
                }
              />
            )}

            {/* Comments */}
            {options && (
              <div>
                <Label
                  forField="ac-comments"
                  label={options.comments.isMandatory ? mandatoryLabel("Comments") : "Comments"}
                />
                <Input
                  id="ac-comments"
                  multiline
                  rows={3}
                  maxLength={8000}
                  disabled={options.comments.isReadOnly}
                  inErrorState={Boolean(errors.comments)}
                  {...register("comments", {
                    ...fieldRules(options.comments, "Comments"),
                    maxLength: { value: 8000, message: "Comments must be 8000 characters or less" },
                  })}
                />
                <ErrorLabel error={errors.comments?.message} />
              </div>
            )}

            {/* Primary Contact */}
            <div>
              <Label forField="ac-primary-contact" label="Primary Contact" />
              <Select id="ac-primary-contact" {...register("primaryContact")}>
                <option value="0">No</option>
                <option value="1">Yes</option>
              </Select>
            </div>

            {/* User-defined groups */}
            {options?.userDefinedGroups && options.userDefinedGroups.length > 0 && (
              <hr className="border-gray-200 my-6" />
            )}
            {options?.userDefinedGroups?.map((group) => (
              <div key={group.id} className="rounded-md border border-gray-200 p-3">
                {group.name && (
                  <h3 className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-3">
                    {group.name}
                  </h3>
                )}
                <div className="space-y-3">
                  {group.items.map((item) => (
                    <UserDefinedItemField
                      key={item.id}
                      item={item}
                      fieldKey={`ud_${item.itemId}`}
                      register={register}
                      control={control}
                      errors={errors}
                      loadOptions={item.isAutoComplete ? (q) => searchContactUdLookup(item.itemId, q).then(toSelectOptions) : undefined}
                    />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </FormDialog>
  );
};
